'use client'

import { useEffect, useRef, useState } from 'react'
import DOMPurify from 'src/_helpers/dompurify'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Tabs from 'src/components/design-system/Tabs'
import Typography from 'src/components/design-system/Typography'
import {
  joinTranslatedTitles,
  JournalInfoGrid,
  JournalIssueAside,
  JournalLicenseLinks,
  JournalLicenseModels,
  JournalLogoImage,
} from 'src/components/journals/components/JournalHeaderShared'
import { gsap } from 'gsap'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import useJournalDomainRoutes from './useJournalDomainRoutes'
import EditorialPanelEntryButtons from 'src/components/journals/components/EditorialPanelEntryButtons'

function mediaUrl(media) {
  return media?.url ? getMediaUrl(media.url, media.version) : null
}

export default function DomainJournalContent({ journal, newestIssue, newestVolume, title, translation, locale }) {
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const [activeTab, setActiveTab] = useState('info')
  const infoRef = useRef(null)
  const aboutRef = useRef(null)
  const issueTranslation = newestIssue ? getPreferredTranslation(newestIssue, locale) || {} : {}
  const description = translation.description ? DOMPurify.sanitize(translation.description) : ''
  const fallbackDescription = translation.subtitle ? DOMPurify.sanitize(translation.subtitle) : ''
  const coverMedia =
    journal.frontCover || newestIssue?.thumbs?.detail || journal.thumbs?.list || journal.thumbs?.cms_preview
  const coverMediaLarge =
    journal.frontCover || newestIssue?.thumbs?.fullscreen || journal.thumbs?.list || journal.thumbs?.cms_preview
  const issueCover = mediaUrl(coverMedia)
  const issueCoverLarge = mediaUrl(coverMediaLarge) || issueCover
  const issueCoverWidth = coverMedia?.width || 309
  const issueCoverHeight = coverMedia?.height || 426
  const issueCoverLargeWidth = coverMediaLarge?.width || issueCoverWidth
  const issueCoverLargeHeight = coverMediaLarge?.height || issueCoverHeight
  const issueAlt = issueTranslation.alt || issueTranslation.title || title
  const issueTitle = newestIssue?.number
    ? `${translate('T_NUMBER')} ${newestIssue.number}`
    : issueTranslation.title || newestIssue?.title
  const issueSubtitle = issueTranslation.subtitle
  const issueYear = newestIssue?.releaseYear
  const newestVolumeTranslation = getPreferredTranslation(newestVolume, locale) || {}
  const newestVolumeTitle = newestVolumeTranslation.title
  const issuesHref = buildLocalizedPath(journalRoutes.ISSUES.LIST, locale)
  const issueHref = issueTranslation.slug
    ? buildLocalizedPath(journalRoutes.ISSUES.DETAIL, locale, { issue: issueTranslation.slug })
    : issuesHref
  const licenceModels = Array.isArray(journal.licencesModels) ? journal.licencesModels : []
  const licences = Array.isArray(journal.licenses) ? journal.licenses : []
  const doiPrefixes = Array.isArray(journal.doiPrefixes) ? journal.doiPrefixes.filter(Boolean) : []
  const publishers = Array.isArray(journal?.publishers) ? journal.publishers : []
  const editorInChiefTranslation = getPreferredTranslation(journal.editorInChief, locale) || {}
  const editorInChief = journal.editorInChief?.fullName
    ? `${editorInChiefTranslation?.academicPrefix || ''} ${journal.editorInChief.fullName}`.trim()
    : null
  const infoItems = [
    {
      label: 'ISSN / eISSN',
      content:
        journal.issn || journal.eIssn ? (
          <>
            {journal.issn && <p>ISSN {journal.issn}</p>}
            {journal.eIssn && <p>eISSN {journal.eIssn}</p>}
          </>
        ) : null,
    },
    ...(doiPrefixes.length > 0
      ? [
          {
            label: translate('T_DOI_PREFIXES'),
            content: doiPrefixes.map((prefix, prefixIndex) => <p key={`doi-prefix-${prefixIndex}`}>{prefix}</p>),
          },
        ]
      : []),
    { type: 'divider' },
    {
      label: translate('T_LICENSE_MODEL'),
      content: joinTranslatedTitles(licenceModels, locale) ? (
        <JournalLicenseModels
          items={licenceModels}
          locale={locale}
          itemClassName="mr-8 inline-flex items-center gap-4"
          separator=","
        />
      ) : null,
    },
    {
      label: translate('T_LICENSE_TYPE'),
      content: joinTranslatedTitles(licences, locale) ? (
        <JournalLicenseLinks
          items={licences}
          locale={locale}
          linkClassName="mr-8 underline"
          separator=","
          emptyHref="#"
        />
      ) : null,
    },
    { type: 'divider' },
    { label: translate('T_FIELD'), content: joinTranslatedTitles(journal.fields, locale) },
    { label: translate('T_DISCIPLINE'), content: joinTranslatedTitles(journal.disciplines, locale) },
    { label: translate('T_PUBLISHER'), content: joinTranslatedTitles(publishers, locale) },
    {
      label: translate('T_AFFILIATING_INSTITUTION'),
      content: joinTranslatedTitles(journal.affiliations, locale),
    },
    { type: 'divider' },
    {
      label: translate('T_LANGUAGE_OF_PUBLICATION'),
      content: joinTranslatedTitles(journal.publicationLanguages, locale),
    },
    { label: translate('T_JOURNAL_RELEASE_YEAR'), content: journal.releaseYear },
    { label: translate('T_EDITOR_IN_CHIEF'), content: editorInChief },
    { label: translate('T_PUBLICATION_FREQUENCY'), content: translation.periodicity },
  ]

  useEffect(() => {
    const activeEl = activeTab === 'about' ? aboutRef.current : infoRef.current

    if (!activeEl) {
      return
    }

    gsap.fromTo(activeEl, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power3.out' })
  }, [activeTab])

  return (
    <>
      <div className="mx-auto mt-[var(--header-height)] flex w-full max-w-screen-2xl items-start justify-between gap-40 px-16 pb-32 pt-45 d:px-40 d:pt-64">
        <div className="flex flex-col gap-y-24">
          <Typography variant="h1-bold" color="text-basic-black" className="max-w-665">
            {title}
          </Typography>
          <EditorialPanelEntryButtons journal={journal} isDomainJournal />
        </div>
        <JournalLogoImage media={journal.logo3} alt={title} className="hidden max-h-70 d:block" />
      </div>

      <div className="mx-auto grid w-full max-w-screen-2xl gap-40 px-16 pb-45 d:grid-cols-[3fr_9fr] d:px-40 d:pb-64">
        <JournalIssueAside
          title={translate('T_NEWEST_ISSUE')}
          cover={issueCover}
          coverLarge={issueCoverLarge}
          coverWidth={issueCoverWidth}
          coverHeight={issueCoverHeight}
          coverLargeWidth={issueCoverLargeWidth}
          coverLargeHeight={issueCoverLargeHeight}
          alt={issueAlt}
          issueTitle={issueTitle}
          issueSubtitle={issueSubtitle}
          volumeTitle={newestVolumeTitle}
          year={issueYear}
          issueHref={issueHref}
          issuesHref={issuesHref}
          translate={translate}
        />

        <section className="flex flex-col gap-y-40 d:border-l d:border-dark-blue-dark-blue6 d:pl-40 d:pr-76">
          <Tabs
            tabs={[
              { id: 'info', label: translate('T_BASIC_INFORMATION') || 'Main information' },
              (description || fallbackDescription) && { id: 'about', label: translate('T_DESCRIPTION') || 'About' },
            ]}
            activeTab={activeTab}
            className="flex-wrap gap-y-10 border-b border-basic-black d:flex"
            color="journalDomain"
            onTabChange={setActiveTab}
          />

          {activeTab === 'about' ? (
            <div
              ref={aboutRef}
              className="text-block-content article-b2 max-w-[659px] text-basic-black"
              dangerouslySetInnerHTML={{ __html: description || fallbackDescription }}
            />
          ) : (
            <JournalInfoGrid
              ref={infoRef}
              items={infoItems}
              className="grid max-w-[660px] gap-y-32 d:grid-cols-2 d:gap-x-40"
            />
          )}
        </section>
      </div>
    </>
  )
}
